// =====================================================
// PATIENT TYPES - Multi-Centro + Nº Historia
// =====================================================

export type DocumentType = 'DNI' | 'NIE' | 'PASSPORT' | 'OTHER';
export type ContactPreference = 'PHONE' | 'EMAIL' | 'WHATSAPP' | 'SMS';

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  DNI: 'DNI',
  NIE: 'NIE',
  PASSPORT: 'Pasaporte',
  OTHER: 'Otro',
};

export const CONTACT_PREF_LABELS: Record<ContactPreference, string> = {
  PHONE: 'Teléfono',
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
};

export interface InsuranceCompanyInfo {
  id: string;
  code: string;
  name: string;
}

export interface LinkedSiteInfo {
  id: string;
  siteId: string;
  siteName: string;
  siteCode?: string;
  medicalRecordNumber?: string;
  isActive: boolean;
  linkedAt: string;
  linkedByUserId?: string;
  linkedByUserName?: string;
}

export interface Patient {
  id: string;
  companyId: string;
  createdBySiteId: string;
  createdBySiteName?: string;
  siteId?: string;
  insuranceCompanyId: string;
  insuranceCompany: InsuranceCompanyInfo;
  firstName: string;
  lastName: string;
  fullName: string;
  documentType: DocumentType;
  documentNumber: string;
  birthDate: string;
  age: number;
  phonePrimary: string;
  email: string;
  contactPref: ContactPreference;
  phoneSecondary?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressFloor?: string;
  postalCode?: string;
  city?: string;
  province?: string;
  country?: string;
  fullAddress?: string;
  adminNotes?: string;
  isActive: boolean;
  isActiveInCurrentSite?: boolean;
  medicalRecordNumber?: string;
  linkedSites?: LinkedSiteInfo[];
  createdAt: string;
  updatedAt: string;
  budgetsCount?: number;
}

export interface PatientListItem {
  id: string;
  fullName: string;
  documentType: DocumentType;
  documentNumber: string;
  phonePrimary: string;
  email: string;
  insuranceCompanyId: string;
  insuranceCompanyName: string;
  isActive: boolean;
  isActiveInCurrentSite?: boolean;
  medicalRecordNumber?: string;
  linkedSitesCount?: number;
  hasPendingReminders?: boolean;
  createdAt: string;
}

export interface PaginatedPatients {
  data: PatientListItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface PatientSearchResult {
  id: string;
  fullName: string;
  documentNumber: string;
  matchedBy: 'document' | 'email' | 'phone';
}

export interface PatientCompanySearchResult {
  id: string;
  fullName: string;
  documentType: string;
  documentNumber: string;
  email: string;
  phonePrimary: string;
  insuranceCompanyName: string;
  isActiveGlobal: boolean;
  matchedBy: 'document' | 'email' | 'phone';
  linkedSites: {
    siteId: string;
    siteName: string;
    isActive: boolean;
    linkedAt: string;
    medicalRecordNumber?: string;
  }[];
  isLinkedToCurrentSite: boolean;
  isActiveInCurrentSite: boolean;
}

export interface PatientLinkResult {
  success: boolean;
  patientId: string;
  siteId: string;
  siteName: string;
  medicalRecordNumber?: string;
  isActive: boolean;
  linkedAt: string;
  linkedByUserId?: string;
  linkedByUserName?: string;
}

export interface PatientQueryParams {
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
  isActive?: boolean;
  includeInactiveInSite?: boolean;
}

export interface PatientSearchParams {
  query: string;
  searchBy?: 'document' | 'email' | 'phone' | 'any';
}

export interface PatientFormData {
  firstName: string;
  lastName: string;
  documentType: DocumentType;
  documentNumber: string;
  birthDate: string;
  phonePrimary: string;
  email: string;
  contactPref: ContactPreference;
  insuranceCompanyId: string;
  medicalRecordNumber?: string;
  phoneSecondary?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressFloor?: string;
  postalCode?: string;
  city?: string;
  province?: string;
  country?: string;
  adminNotes?: string;
}

export interface CreatePatientRequest extends PatientFormData {}
export interface UpdatePatientRequest extends Partial<PatientFormData> {
  isActive?: boolean;
}

export interface LinkPatientData {
  patientId: string;
  medicalRecordNumber?: string;
}

// =====================================================
// FICHA 360° - Tipos agregados (v1)
// =====================================================

/**
 * KPIs agregados del paciente para la cabecera de la ficha 360°.
 * Calculado en el centro activo del usuario.
 */
export interface PatientSummary {
  totalDebt: number;
  totalBudgetsCount: number;
  activeBudgetsCount: number;
  acceptedBudgetsCount: number;
  stalledSentBudgetsCount: number;
  plannedRemindersCount: number;
  failedRemindersUnmanaged: number;
  nextReminderDate: string | null;
  totalSitesCount: number;
  activeSitesCount: number;
  lastActivityAt: string | null;
  lastActivityLabel: string | null;
}

/**
 * Punto mensual de la gráfica financiera.
 */
export interface MonthlyAggregate {
  year: number;
  month: number;
  label: string;
  billed: number;
  collected: number;
}

/**
 * Resumen financiero agregado + evolución mensual (últimos 12 meses).
 */
export interface PatientFinancialSummary {
  totalBilled: number;
  totalCollected: number;
  totalPending: number;
  totalRefunded: number;
  monthly: MonthlyAggregate[];
}

// =====================================================
// FICHA 360° v2 — Notas internas + Timeline
// =====================================================

export interface PatientNote {
  id: string;
  patientId: string;
  siteId: string;
  content: string;
  pinned: boolean;
  createdById: string;
  createdByName: string | null;
  editedAt: string | null;
  editedById: string | null;
  editedByName: string | null;
  editCount: number;
  canEdit: boolean;
  canDelete: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PatientNoteEdit {
  id: string;
  noteId: string;
  previousContent: string;
  editedById: string;
  editedByName: string | null;
  editedAt: string;
}

export type TimelineEventType =
  | 'BUDGET_CREATED'
  | 'BUDGET_STATUS_CHANGED'
  | 'PAYMENT'
  | 'REMINDER'
  | 'CONTACT'
  | 'SITE_LINK'
  | 'DOCUMENT'
  | 'NOTE'
  | 'PATIENT_EDIT';

export interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  occurredAt: string;
  siteId: string | null;
  siteName: string | null;
  userId: string | null;
  userName: string | null;
  title: string;
  description: string | null;
  budgetId?: string | null;
  budgetNumber?: string | null;
  reminderId?: string | null;
  contactId?: string | null;
  noteId?: string | null;
  documentId?: string | null;
  amount?: number | null;
  meta?: Record<string, any>;
}

export const TIMELINE_EVENT_LABELS: Record<TimelineEventType, string> = {
  BUDGET_CREATED: 'Presupuestos',
  BUDGET_STATUS_CHANGED: 'Cambios de estado',
  PAYMENT: 'Pagos',
  REMINDER: 'Recordatorios',
  CONTACT: 'Contactos',
  SITE_LINK: 'Centros',
  DOCUMENT: 'Documentos',
  NOTE: 'Notas',
  PATIENT_EDIT: 'Edición paciente',
};

